import { useRef, useState } from 'react'
import { Modal } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { ModalHeader } from '@/components/modal/ModalHeader'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'

type ResearchContactDialogProps = {
  onClose: () => void
  onSubmit: () => Promise<void>
}

export default function ResearchContactDialog({ onClose, onSubmit }: ResearchContactDialogProps) {
  const dispatch = useAppDispatch()
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submitting = useRef(false)

  async function handleSubmit() {
    if (submitting.current) return
    submitting.current = true
    setSending(true)
    setError(null)
    try {
      await onSubmit()
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Thanks! Our research team may contact you.', durationMs: kToastDurationShortMs }))
      onClose()
    } catch {
      setError('Your contact request could not be sent. Please try again.')
    } finally {
      submitting.current = false
      setSending(false)
    }
  }

  return (
    <Modal open onOpenChange={(open) => !open && !submitting.current && onClose()}>
      <ModalContainer width="md" height="max-sm" className="max-w-md" preventDismiss={sending}>
        <div className="flex min-h-0 flex-col gap-6 overflow-auto p-6">
          <ModalHeader title="Want to tell us more?" subtitle="Our research team meets with users to learn more about how they work with Paxton." />
          <ModalFooterWithActions
            secondaryAction={{ label: 'Not now', onClick: onClose, disabled: sending }}
            primaryAction={{ label: 'Yes, reach out', onClick: handleSubmit, loading: sending, disabled: sending }}
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
          />
        </div>
      </ModalContainer>
    </Modal>
  )
}
